import { EditorEditAction } from "@common/lib/chat-ai/ai-action-dialog/ai-action-dialog.component";
import { AiEditorActionsService, IBaseChatEditorActionParameterType } from "@common/lib/chat-ai/ai-editor-actions.service";
import { StringUtilities } from "@common/lib/utilities/string-utilities";
import { SimpleValueType } from "@common/ux/edit-simple-value-breeze-entity-dialog/edit-simple-value-breeze-entity-dialog.component";
import { FroalaActionUtilities } from "@common/ux/html-editor/html-editor-froala/froala-action-utilities";
import { Dropdown } from "bootstrap";
import FroalaEditor from "froala-editor";
import { lastValueFrom } from "rxjs";

const commandName = "ai";

interface IListItemOptions {
    name: string;
    value: string;
    value2?: string;
    disabled?: boolean;
    submenuItems?: Pick<IListItemOptions, "name" | "value" | "disabled">[];
    isDropDownItem?: boolean;
}

const buildListItem = (options: IListItemOptions): HTMLLIElement => {
    const { name, value, value2, disabled = false, submenuItems = [], isDropDownItem = false } = options;

    const li = document.createElement("li");
    const anchor = document.createElement("a");
    anchor.title = name;
    anchor.textContent = name;

    if (disabled) {
        anchor.classList.add("fr-disabled");
    }
    if (isDropDownItem) {
        anchor.classList.add("dropdown-item");
    }

    if (submenuItems.length > 0) {
        anchor.classList.add("fr-submenu", "align-items-center", "gap-3");
        anchor.dataset.bsToggle = "dropdown";

        const icon = document.createElement("i");
        icon.classList.add("ms-auto", "fal", "fa-angle-right");
        anchor.appendChild(icon);

        const submenu = document.createElement("ul");
        submenu.classList.add("dropdown-menu", "bg-white");

        for (const item of submenuItems) {
            submenu.appendChild(buildListItem({
                name: item.name,
                value,
                value2: item.value,
                disabled: item.disabled ?? disabled,
                isDropDownItem: true,
            }));
        }

        li.appendChild(anchor);
        li.appendChild(submenu);
    } else {
        anchor.classList.add("fr-command");
        anchor.dataset.cmd = commandName;
        anchor.dataset.param1 = value;

        if (value2) {
            anchor.dataset.param2 = value2;
        }

        li.appendChild(anchor);
    }

    return li;
};

export class AIFroalaAction {
    public static registerAction(aiEditorActionsService: AiEditorActionsService) {
        FroalaEditor.DefineIconTemplate("font_awesome_light", `<i class="fal fa-[NAME]" aria-hidden="true"></i>`);
        FroalaEditor.DefineIcon("ai", { NAME: "sparkles" });

        FroalaEditor.RegisterCommand(commandName, {
            title: "AI actions",
            type: "dropdown",
            icon: "ai",
            options: {},
            async callback(_buttonName: string, param: string, param2: string) {
                const action = Object.values(aiEditorActionsService.getEditorActions())
                    .concat(this.opts.customEditorActions ?? [])
                    .find((a) => a.type === param);
                if (!action) {
                    return;
                }

                const editor = this;
                const selectionSnapshot = FroalaActionUtilities.captureSelection(editor);
                // save the current selection
                this.selection.save();

                // keep the selected markup so formatting survives the AI round-trip.
                const selectedText = this.selection.text().trim();
                let text = selectedText;
                if (!text) {
                    // no text selected, get the whole editor content
                    text = this.html.get().trim();
                } else {
                    // keep the selected markup so formatting survives the AI round-trip.
                    text = this.html.getSelected().trim();
                }

                if (!text) {
                    return;
                }

                const params: IBaseChatEditorActionParameterType = {};
                if (param2) {
                    Object.assign(params, JSON.parse(param2));
                }

                const result = await lastValueFrom(aiEditorActionsService.openAiActionDialog({
                    action,
                    text,
                    params,
                    customActions: this.opts.customEditorActions ?? [],
                }), { defaultValue: undefined });
                if (!result) {
                    return;
                }

                FroalaActionUtilities.restoreSelection(editor, selectionSnapshot);
                const hasSelection = !!selectedText;
                const editorElement = this.el as Element;
                const editorTextLengthBeforeInsert = StringUtilities.htmlToPlaintext(this.html.get()).trim().length;
                const selectionStartTextOffset = selectionSnapshot?.startTextOffset ?? 0;
                const selectionEndTextOffset = selectionSnapshot?.endTextOffset ?? selectionStartTextOffset;
                const resultTextLength = StringUtilities.htmlToPlaintext(result.result).trim().length;
                const selectInsertedText = (startTextOffset: number) =>
                    FroalaActionUtilities.selectTextOffsets(editor, startTextOffset, startTextOffset + resultTextLength);
                if (result.action === EditorEditAction.Replace) {
                    if (hasSelection) {
                        this.html.insert(result.result);
                        selectInsertedText(selectionStartTextOffset);
                    } else {
                        this.html.set(result.result);
                        selectInsertedText(0);
                    }
                } else if (result.action === EditorEditAction.InsertBefore) {
                    if (hasSelection) {
                        const selectedElement = this.selection.element();
                        this.selection.setAtStart(selectedElement);
                        this.html.insert(result.result);
                        selectInsertedText(selectionStartTextOffset);
                    } else {
                        this.selection.setAtStart(editorElement);
                        this.html.insert(result.result);
                        selectInsertedText(0);
                    }
                } else if (result.action === EditorEditAction.InsertAfter) {
                    if (hasSelection) {
                        const selectedElement = this.selection.element();
                        this.selection.setAtEnd(selectedElement);
                        this.html.insert(result.result);
                        selectInsertedText(selectionEndTextOffset);
                    } else {
                        this.selection.setAtEnd(editorElement);
                        this.html.insert(result.result);
                        selectInsertedText(editorTextLengthBeforeInsert);
                    }
                }

                // allow undoing this
                this.undo.saveStep();
            },
            refreshOnShow(_button: JQuery, $dropdown: JQuery) {
                const list = $dropdown.find("ul.fr-dropdown-list");

                // Dispose existing dropdowns before creating new ones
                if (this.dropdowns) {
                    this.dropdowns.forEach((d: Dropdown) => d.dispose());
                }
                this.dropdowns = [] as Dropdown[];

                list.empty();

                const text = this.selection.text().trim() || StringUtilities.htmlToPlaintext(this.html.get());
                const disabled = !text;

                const actions = Object.values(aiEditorActionsService.getEditorActions())
                    .concat(this.opts.customEditorActions ?? []);
                for (const option of actions) {
                    const submenuItems = Object.entries(option.paramDetails ?? {})
                        .filter(([_, param]) => param.type === SimpleValueType.Select)
                        .map(([key, param]) => (param.selectValues ?? []).map((opt) => ({
                            name: opt.text,
                            value: JSON.stringify({ [key]: opt.value }),
                            disabled,
                        })))
                        .flat();

                    const item = buildListItem({
                        name: option.displayText,
                        value: option.type,
                        disabled,
                        submenuItems,
                    });

                    if (!submenuItems.length) {
                        // hide any dropdowns when selecting a command.
                        // clicking on a dropdown item already does this, but this doesn't happen when the dropdown is open and you click on a non-dropdown item.
                        const commandItem = item.querySelector(".fr-command");
                        if (commandItem) {
                            commandItem.addEventListener("click", () => this.dropdowns?.forEach((d: Dropdown) => d.hide()));
                        }
                    } else {
                        // create a Dropdown instance for submenu
                        const submenuItem = item.querySelector(".fr-submenu");
                        if (submenuItem) {
                            this.dropdowns.push(Dropdown.getOrCreateInstance(submenuItem, {
                                offset: [0, -2],
                                popperConfig: (bsConfig) => ({
                                    ...bsConfig,
                                    placement: "right-start",
                                    strategy: "fixed",
                                }),
                            }));

                            submenuItem.addEventListener("show.bs.dropdown", () => submenuItem.classList.add("fr-submenu-open"));
                            submenuItem.addEventListener("hide.bs.dropdown", () => submenuItem.classList.remove("fr-submenu-open"));
                        }
                    }

                    list.append(item);
                }
            },
        });
    }
}
